import { PureComponent, Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Icon, Modal, Tab, Form } from 'semantic-ui-react';
import { Formik } from 'formik';

import {
  getMethodsWithTranslations,
  updateMethods,
} from '../../../../store/actions';

import { CustomTable } from '../../../../components';
import MultiLanguageInput from '../../../../components/form/inputs/multiLanguageInput';
import MultiLanguageCKEditor from '../../../../components/form/inputs/multiLanguageCKEditor';
import { SUPPORTED_LANGUAGES } from '../../../../i18n';

import {
  initialValues,
  methodValidation,
  convertToBackendFormat,
} from '../../../../validations/evaluationMethodValidation';

class EvaluationMethods extends Component {
  componentDidMount() {
    const { getMethodsWithTranslations } = this.props;
    getMethodsWithTranslations();
  }

  renderColumns() {
    const { t, i18n } = this.props;
    const columns = [
      {
        title: (
          <div>
            <strong>{t('common.name')}</strong>
          </div>
        ),
        render: (data) => {
          // Handle multilingual details - show current language with English as fallback
          const displayDetails =
            data.translations?.details?.[i18n.language] ||
            data.translations?.details?.en ||
            data.details;
          return <div>{displayDetails}</div>;
        },
        width: 8,
      },
      {
        title: (
          <div>
            <strong>{t('common.date_created')}</strong>
          </div>
        ),
        render: (data) => {
          return (
            <div>
              {new Intl.DateTimeFormat(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              }).format(new Date(data.createdAt))}
            </div>
          );
        },
        width: 3,
      },
      {
        title: (
          <div>
            <strong>{t('common.date_updated')}</strong>
          </div>
        ),
        render: (data) => {
          return (
            <div>
              {new Intl.DateTimeFormat(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              }).format(new Date(data.updatedAt))}
            </div>
          );
        },
        width: 3,
      },
      {
        title: '',
        render: (data) => {
          return <EditEvaluationModal {...this.props} item={data} />;
        },
        width: 2,
      },
    ];

    return columns;
  }

  render() {
    const { methodsWithTranslations, t } = this.props;

    return (
      <div className="view-member-eval-qs ">
        <CustomTable
          header
          customClass="super-admin-table tbody-hover"
          columns={this.renderColumns()}
          data={methodsWithTranslations}
          emptyMessage={t('admin.evaluation_methods.no_methods')}
          keyExtractor={(item, index) => item.id}
        />
      </div>
    );
  }
}

class EditEvaluationModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
      form: {
        details: '',
        method: '',
        response: '',
        preamble: '',
      },
    };
  }

  editSelectedQuestion(form) {
    this.setState({
      form,
    });
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  render() {
    const { modal, form } = this.state;
    const { item, t } = this.props;

    return (
      <Modal
        open={modal}
        onOpen={() => this.toggle()}
        closeIcon={
          <Icon
            onClick={() => this.toggle()}
            name="close"
            className="closeicon"
          />
        }
        size="small"
        trigger={
          <label
            className="activity-actions-warn mb-0"
            onClick={() => this.editSelectedQuestion(item)}
          >
            <Icon name="edit" />
            {t('actions.edit')}
          </label>
        }
        className="edit-evaluation-method"
      >
        <Modal.Header>
          {t('specific_actions.edit_evaluation_method')}
        </Modal.Header>
        <Modal.Content>
          <EditEvaluationForm
            toggle={() => this.toggle()}
            form={form}
            {...this.props}
          />
        </Modal.Content>
      </Modal>
    );
  }
}

class EditEvaluationForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      currentLanguage: 'en',
    };
  }

  async handleValidSubmit(values) {
    try {
      const { form, updateMethods, toggle } = this.props;

      // Convert multilingual format to API format using validation helper
      const submitData = convertToBackendFormat(values);

      await updateMethods(form.id, submitData);
      toggle();
    } catch (error) {
      console.error('Edit Method error~', error);
    }
  }

  // Get reference values for translation
  getReferenceValue = (fieldName, values) => {
    if (this.state.currentLanguage === 'en') return '';
    return values[fieldName]?.en || '';
  };

  render() {
    const { toggle, form, methodLoading, t } = this.props;
    const { currentLanguage } = this.state;

    return (
      <div className="multilingual-evaluation-form">
        <Formik
          onSubmit={(values) => this.handleValidSubmit(values)}
          initialValues={initialValues(form)}
          validationSchema={methodValidation(t)}
          context={{ isMultiLang: true }}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            // Create language tabs
            const languageTabs = SUPPORTED_LANGUAGES.map(
              (lang) =>
                lang.menu && {
                  menuItem: {
                    key: lang.locale,
                    content: (
                      <span>
                        {lang.flag} {lang.name}
                        {lang.locale === 'en' && (
                          <span style={{ color: '#f39c12' }}> *</span>
                        )}
                      </span>
                    ),
                  },
                  render: () => (
                    <Tab.Pane key={lang.locale}>
                      <Form onSubmit={handleSubmit} className="venue-form">
                        <MultiLanguageInput
                          id="details"
                          name="details"
                          placeholder={t('formulary.placeholder.enter_title')}
                          labelText={t('formulary.title')}
                          requiredStar
                          currentLanguage={lang.locale}
                          referenceValue={this.getReferenceValue(
                            'details',
                            values
                          )}
                        />

                        <MultiLanguageInput
                          id="method"
                          name="method"
                          labelText={t('admin.evaluation_methods.method')}
                          currentLanguage={lang.locale}
                          referenceValue={this.getReferenceValue(
                            'method',
                            values
                          )}
                          disabled
                        />

                        <MultiLanguageInput
                          id="response"
                          name="response"
                          labelText={t(
                            'admin.evaluation_methods.respondent_type'
                          )}
                          currentLanguage={lang.locale}
                          referenceValue={this.getReferenceValue(
                            'response',
                            values
                          )}
                          disabled
                        />

                        <MultiLanguageCKEditor
                          id="preamble"
                          name="preamble"
                          labelText={t('common.preamble')}
                          currentLanguage={lang.locale}
                          referenceValue={this.getReferenceValue(
                            'preamble',
                            values
                          )}
                        />

                        <div className="gap-between-ck">
                          <MultiLanguageCKEditor
                            id="facilitatorNote"
                            name="facilitatorNote"
                            labelText={t(
                              'admin.evaluation_methods.facilitator_notes'
                            )}
                            currentLanguage={lang.locale}
                            referenceValue={this.getReferenceValue(
                              'facilitatorNote',
                              values
                            )}
                          />
                        </div>

                        <div className="model-buttons">
                          <Button
                            className="Secondary-Button"
                            type="button"
                            onClick={() => toggle()}
                          >
                            {t('actions.cancel')}
                          </Button>
                          <Button
                            content={t('actions.save')}
                            type="submit"
                            className="Primary-Button"
                            loading={methodLoading}
                            disabled={methodLoading}
                          />
                        </div>
                      </Form>
                    </Tab.Pane>
                  ),
                }
            );

            return (
              <Tab
                panes={languageTabs}
                onTabChange={(e, { activeIndex }) => {
                  const selectedLang = SUPPORTED_LANGUAGES[activeIndex];
                  this.setState({ currentLanguage: selectedLang.locale });
                }}
              />
            );
          }}
        </Formik>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    methodsWithTranslations: state.extra.methodsWithTranslations,
    methodLoading: state.admin.methodLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getMethodsWithTranslations,
      updateMethods,
    },
    dispatch
  );
};

EvaluationMethods = connect(
  mapStateToProps,
  mapDispatchToProps
)(EvaluationMethods);
EvaluationMethods = withTranslation()(EvaluationMethods);

export default EvaluationMethods;
